import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Activity,
  AlertCircle,
  CheckCircle,
  CreditCard,
  DollarSign,
  Eye,
  FileText,
  Plus,
  RefreshCcw,
  Search,
  TrendingUp,
  Upload,
  X,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  addBillReceiptThunk,
  clearReceiptHistory,
  clearSelectedBill,
  createManualBillThunk,
  fetchBillingLookupsThunk,
  fetchBillingConfigurationThunk,
  fetchBillReceiptsThunk,
  fetchPanchayathBillByIdThunk,
  fetchPanchayathBillsThunk,
  fetchGenerationHistoryThunk,
  generateSubscriptionBillsThunk,
  saveBillInvoiceThunk,
} from "../../../store/slices/panchayathBillingSlice";
import { fetchPanchayathsThunk } from "../../../store/slices/panchayathMasterSlice";
import { approveReceiptThunk } from "../../../store/slices/receiptApprovalsSlice";

import "../../../css/master-common.css";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const labelize = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizeTaxType = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "-");

  if (raw === "gst") return "GST";
  if (raw === "non-gst" || raw === "nongst") return "Non-GST";
  return String(value || "").trim();
};

const isGstBill = (bill) =>
  normalizeTaxType(bill?.tax_type) === "GST" &&
  Number(bill?.tax_percentage || 0) > 0;

const formatDateTime = (value) => {
  if (!value) return "-";

  const raw = String(value).trim();
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const API_BASE_URL = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const getInvoiceFileUrl = (filePath) => {
  if (!filePath) return "";
  const value = String(filePath).trim();
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${API_BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
};

const EMPTY_FILTERS = {
  page: 1,
  pageSize: 10,
  pan_id: "",
  category: "",
  status: "",
  generated_type: "",
  from_date: "",
  to_date: "",
  search: "",
};

const PanchayathBillingPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();

  const panIdActive = useSelector((s) => Number(s.tenant?.pan_id_active || 0));
  // Superadmin payments are approved immediately; others wait for Superadmin.
  const isSuperadmin =
    String(useSelector((s) => s.auth?.user?.role || "")).trim().toLowerCase() === "superadmin";
  const tenantPanIds = useSelector((s) => s.tenant?.pan_ids || []);
  const hasActivePanchayath = panIdActive > 0;

  const normalizedTenantPanIds = useMemo(
    () =>
      (Array.isArray(tenantPanIds) ? tenantPanIds : [])
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0),
    [tenantPanIds]
  );

  const {
    rows = [],
    pagination = { page: 1, pageSize: 10, total: 0, totalPages: 0 },
    lookups = {
      categories: [],
      manual_categories: [],
      payment_modes: [],
      statuses: [],
      settings: {
        gst_percentage: 18,
        subscription_cron_time: "02:00",
        invoice_prefix: "INV",
      },
    },
    loading = false,
    receiptLoading = false,
    savingManualBill = false,
    savingInvoice = false,
    savingReceipt = false,
    generating = false,
    generationHistory = { latestCron: null, rows: [] },
    generationHistoryPagination = {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
    },
    generationHistoryLoading = false,
    selectedBill = null,
    detailLoading = false,
    billingConfiguration = null,
  } = useSelector((s) => s.panchayathBilling || {});

  const {
    rows: panchayaths = [],
    loading: panchayathLoading = false,
  } = useSelector((s) => s.panchayathMaster || {});

  const billingSettings = {
    gst_percentage: Number(lookups?.settings?.gst_percentage) || 18,
    subscription_cron_time: String(
      lookups?.settings?.subscription_cron_time || "02:00"
    ),
    invoice_prefix: String(
      lookups?.settings?.invoice_prefix || "INV"
    ),
  };

  const formatCronTime = (value) => {
    const raw = String(value || "02:00");
    const match = raw.match(/^(\d{2}):(\d{2})$/);
    if (!match) return "2:00 AM";

    const hour = Number(match[1]);
    const minute = match[2];
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute} ${suffix}`;
  };

  const availablePanchayaths = useMemo(() => {
    const allRows = Array.isArray(panchayaths) ? panchayaths : [];

    if (hasActivePanchayath) {
      return allRows.filter((item) => Number(item.pan_id) === panIdActive);
    }

    if (normalizedTenantPanIds.length > 0) {
      const allowed = new Set(normalizedTenantPanIds);
      return allRows.filter((item) => allowed.has(Number(item.pan_id)));
    }

    return allRows;
  }, [panchayaths, hasActivePanchayath, panIdActive, normalizedTenantPanIds]);

  const activePanchayath = useMemo(
    () => panchayaths.find((item) => Number(item.pan_id) === panIdActive) || null,
    [panchayaths, panIdActive]
  );

  const buildUrlFilters = () => ({
    page: 1,
    pageSize: 10,
    pan_id: hasActivePanchayath
      ? String(panIdActive)
      : searchParams.get("pan_id") || "",
    category: searchParams.get("category") || "",
    status: searchParams.get("status") || "",
    generated_type: searchParams.get("generated_type") || "",
    from_date: searchParams.get("from_date") || "",
    to_date: searchParams.get("to_date") || "",
    search: searchParams.get("search") || "",
  });

  const [filters, setFilters] = useState(buildUrlFilters);
  const [manualModal, setManualModal] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [receiptModal, setReceiptModal] = useState(false);
  const [generationHistoryModal, setGenerationHistoryModal] = useState(false);
  const [confirmGenerateModal, setConfirmGenerateModal] = useState(false);
  const [autoApproving, setAutoApproving] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [activeBill, setActiveBill] = useState(null);
  const [receiptData, setReceiptData] = useState({ bill: null, receipts: [] });

  const [generationHistoryFilters, setGenerationHistoryFilters] = useState({
    page: 1,
    pageSize: 10,
    search: "",
    source: "",
    action: "",
    pan_id: hasActivePanchayath ? String(panIdActive) : "",
    from_date: "",
    to_date: "",
  });

  const [manualForm, setManualForm] = useState({
    pan_id: hasActivePanchayath ? String(panIdActive) : "",
    bill_category: "installation",
    bill_amount: "",
    bill_date: today(),
    remarks: "",
  });

  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: "",
    invoice_amount: "",
    invoice_date: today(),
    invoice_file: null,
  });

  const [paymentForm, setPaymentForm] = useState({
    received_amount: "",
    payment_mode: "cash",
    cheque_no: "",
    cheque_date: "",
    bank_name: "",
    transaction_reference: "",
    receipt_date: today(),
    remarks: "",
  });

  useEffect(() => {
    dispatch(fetchBillingLookupsThunk());
    dispatch(fetchBillingConfigurationThunk());
    dispatch(fetchPanchayathsThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchGenerationHistoryThunk({
        page: 1,
        pageSize: 10,
        source: "cron",
        pan_id: hasActivePanchayath ? String(panIdActive) : "",
      })
    );
  }, [dispatch, hasActivePanchayath, panIdActive]);

  useEffect(() => {
    const next = buildUrlFilters();
    setFilters(next);
    dispatch(fetchPanchayathBillsThunk(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchParamsKey, hasActivePanchayath, panIdActive]);

  useEffect(() => {
    setManualForm((prev) => ({
      ...prev,
      pan_id: hasActivePanchayath ? String(panIdActive) : "",
    }));

    setGenerationHistoryFilters((prev) => ({
      ...prev,
      pan_id: hasActivePanchayath ? String(panIdActive) : "",
    }));
  }, [hasActivePanchayath, panIdActive]);

  const loadBills = (nextFilters = filters) => {
    dispatch(
      fetchPanchayathBillsThunk({
        ...nextFilters,
        pan_id: hasActivePanchayath ? String(panIdActive) : nextFilters.pan_id,
      })
    );
  };

  const applyUrlFilters = (next) => {
    const params = new URLSearchParams();
    const effectivePanId = hasActivePanchayath ? String(panIdActive) : next.pan_id;

    if (effectivePanId) params.set("pan_id", effectivePanId);
    if (next.category) params.set("category", next.category);
    if (next.status) params.set("status", next.status);
    if (next.generated_type) params.set("generated_type", next.generated_type);
    if (next.from_date) params.set("from_date", next.from_date);
    if (next.to_date) params.set("to_date", next.to_date);
    if (String(next.search || "").trim()) params.set("search", String(next.search).trim());

    setSearchParams(params);
  };

  const handleSearch = () => {
    if (filters.from_date && filters.to_date && filters.from_date > filters.to_date) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    const next = {
      ...filters,
      page: 1,
      pan_id: hasActivePanchayath ? String(panIdActive) : filters.pan_id,
      search: String(filters.search || "").trim(),
    };

    setFilters(next);
    applyUrlFilters(next);
  };

  const handleReset = () => {
    const next = {
      ...EMPTY_FILTERS,
      pan_id: hasActivePanchayath ? String(panIdActive) : "",
    };
    setFilters(next);
    applyUrlFilters(next);
  };

  const changePage = (page) => {
    const totalPages = Number(pagination.totalPages || 0);
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;

    const next = { ...filters, page };
    setFilters(next);
    loadBills(next);
  };

  // Summary is calculated by the backend across ALL records
  // matching the active filters, not only the current page.
  const summary = {
    totalBill: Number(
      pagination?.summary?.total_billing_amount || 0
    ),
    totalReceived: Number(
      pagination?.summary?.total_received || 0
    ),
    totalBalance: Number(
      pagination?.summary?.total_balance || 0
    ),
  };

  const isPendingSubscriptionFilter =
    filters.category === "subscription" && filters.status === "pending";

  const selectedManualPanchayath = useMemo(() => {
    const selectedPanId = hasActivePanchayath
      ? panIdActive
      : Number(manualForm.pan_id || 0);

    if (!selectedPanId) return null;

    return availablePanchayaths.find(
      (item) => Number(item.pan_id) === Number(selectedPanId)
    ) || null;
  }, [
    availablePanchayaths,
    hasActivePanchayath,
    panIdActive,
    manualForm.pan_id,
  ]);

  const manualTaxPreview = useMemo(() => {
    const baseAmount =
      Number(manualForm.bill_amount || 0);

    const category =
      String(
        manualForm.bill_category || ""
      ).trim().toLowerCase();

    const taxConfig =
      billingConfiguration?.manual_tax?.[
        category
      ] || null;

    const taxType =
      normalizeTaxType(
        taxConfig?.tax_type
      ) || "Non-GST";

    const taxMode =
      String(
        taxConfig?.tax_mode ||
          "percentage"
      ).toLowerCase() === "amount"
        ? "amount"
        : "percentage";

    const taxValue =
      Number(
        taxConfig?.tax_value || 0
      );

    const isGst =
      taxType === "GST";

    let taxAmount = 0;
    let gstPercentage = 0;

    if (
      baseAmount > 0 &&
      isGst
    ) {
      if (
        taxMode === "amount"
      ) {
        taxAmount =
          Math.round(
            (
              taxValue +
              Number.EPSILON
            ) *
              100
          ) / 100;
      } else {
        gstPercentage =
          taxValue;

        taxAmount =
          Math.round(
            (
              baseAmount *
                gstPercentage /
                100 +
              Number.EPSILON
            ) *
              100
          ) / 100;
      }
    }

    const finalAmount =
      Math.round(
        (
          baseAmount +
            taxAmount +
          Number.EPSILON
        ) *
          100
      ) / 100;

    return {
      baseAmount,
      taxType,
      taxMode,
      taxValue,
      isGst,
      gstPercentage,
      taxAmount,
      finalAmount,
    };
  }, [
    manualForm.bill_amount,
    manualForm.bill_category,
    billingConfiguration,
  ]);

  const openManualBill = () => {
    setManualForm({
      pan_id: hasActivePanchayath ? String(panIdActive) : "",
      bill_category: lookups?.manual_categories?.includes("installation")
        ? "installation"
        : lookups?.manual_categories?.[0] || "",
      bill_amount: "",
      bill_date: today(),
      remarks: "",
    });
    setManualModal(true);
  };

  const submitManualBill = async () => {
    const selectedPanId = hasActivePanchayath
      ? String(panIdActive)
      : manualForm.pan_id;

    if (!selectedPanId) return toast.warning("Please select Panchayath");
    if (!manualForm.bill_category) return toast.warning("Please select bill category");
    if (Number(manualForm.bill_amount) <= 0) return toast.warning("Please enter valid bill amount");
    if (!manualForm.bill_date) return toast.warning("Please select bill date");

    try {
      const result = await dispatch(
        createManualBillThunk({
          pan_id: Number(selectedPanId),
          bill_category: manualForm.bill_category,
          bill_amount: Number(manualForm.bill_amount),
          bill_date: manualForm.bill_date,
          remarks: manualForm.remarks,
        })
      ).unwrap();

      const createdBill =
        result?.data ||
        result ||
        {};

      toast.success(
        Number(createdBill?.tax_amount || 0) > 0
          ? `Bill created successfully. GST ${Number(createdBill.tax_percentage || 0)}% added. Final amount ${money(createdBill.bill_amount)}`
          : "Bill created successfully"
      );
      setManualModal(false);

      dispatch(
        fetchGenerationHistoryThunk({
          page: 1,
          pageSize: 10,
          source: "cron",
          pan_id: hasActivePanchayath ? String(panIdActive) : "",
        })
      );

      const next = { ...filters, page: 1 };
      setFilters(next);
      loadBills(next);
    } catch (error) {
      toast.error(error || "Failed to create bill");
    }
  };

  const openInvoice = (bill) => {
    if (!bill) return;
    setActiveBill(bill);
    setInvoiceForm({
      invoice_number: bill.invoice_number || "",
      invoice_amount: bill.bill_amount ?? "",
      invoice_date: bill.invoice_date || today(),
      invoice_file: null,
    });
    setInvoiceModal(true);
  };

  const submitInvoice = async () => {
    if (!activeBill) return;
    if (Number(activeBill.bill_amount) <= 0) return toast.warning("Final bill amount is invalid");
    if (!invoiceForm.invoice_date) return toast.warning("Please select invoice date");

    if (invoiceForm.invoice_file) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(invoiceForm.invoice_file.type)) {
        return toast.warning("Only PDF, JPG, JPEG and PNG invoice files are allowed");
      }
      if (invoiceForm.invoice_file.size > 10 * 1024 * 1024) {
        return toast.warning("Invoice file size cannot exceed 10 MB");
      }
    }

    try {
      await dispatch(
        saveBillInvoiceThunk({
          billId: activeBill.bill_id,
          payload: {
            invoice_number: invoiceForm.invoice_number,
            invoice_amount: Number(activeBill.bill_amount),
            invoice_date: invoiceForm.invoice_date,
            invoice_file: invoiceForm.invoice_file,
          },
        })
      ).unwrap();

      toast.success("Invoice saved successfully");
      setInvoiceModal(false);
      setActiveBill(null);
      loadBills();
    } catch (error) {
      toast.error(error || "Failed to save invoice");
    }
  };

  const openPayment = async (bill) => {
    if (!bill) return;

    if (!bill.invoice_number || Number(bill.invoice_amount) <= 0) {
      toast.warning("Add the invoice first, then record the payment");
      return;
    }

    if (bill.status === "cleared") {
      toast.info("Bill is already cleared");
      return;
    }

    if (bill.status === "cancelled") {
      toast.warning("A cancelled bill cannot receive payments");
      return;
    }

    try {
      const result = await dispatch(fetchBillReceiptsThunk(bill.bill_id)).unwrap();
      const latestBill = result?.bill ? { ...bill, ...result.bill } : bill;
      const available = Number(
        latestBill.available_for_submission ?? latestBill.balance_amount ?? 0
      );

      if (available <= 0) {
        if (Number(latestBill.pending_received || 0) > 0) {
          toast.info("The remaining amount is already recorded and waiting for approval");
        } else {
          toast.info("Nothing left to record. This bill is fully paid or waiting for approval");
        }
        return;
      }

      setActiveBill(latestBill);
      setPaymentForm({
        received_amount: "",
        payment_mode: "cash",
        cheque_no: "",
        cheque_date: "",
        bank_name: "",
        transaction_reference: "",
        receipt_date: today(),
        remarks: "",
      });
      setPaymentModal(true);
    } catch (error) {
      toast.error(error || "Failed to load current payment details");
    }
  };

  // Finds the id of the receipt just created by Record Payment.
  // Uses the id returned by the save API when present; otherwise reloads the
  // bill's receipts and picks the newest pending receipt with the same amount.
  const findNewReceiptId = async (saved, billId, amount) => {
    const data = saved?.data || {};
    const directId =
      data.receipt_id ||
      data.receipt?.receipt_id ||
      saved?.receipt_id ||
      null;
    if (directId) return directId;

    try {
      const result = await dispatch(fetchBillReceiptsThunk(billId)).unwrap();
      const pending = (result?.receipts || [])
        .filter(
          (item) =>
            String(item.approval_status || "pending").toLowerCase() === "pending" &&
            Math.abs(Number(item.received_amount || 0) - Number(amount)) < 0.005
        )
        .sort((a, b) => Number(b.receipt_id || 0) - Number(a.receipt_id || 0));
      return pending[0]?.receipt_id || null;
    } catch (error) {
      return null;
    }
  };

  const submitPayment = async () => {
    if (!activeBill) return;

    const amount = Number(paymentForm.received_amount);
    if (!Number.isFinite(amount) || amount <= 0) return toast.warning("Please enter valid received amount");

    const balance = Number(activeBill.balance_amount || 0);
    const availableForSubmission = Number(activeBill.available_for_submission ?? balance);

    if (availableForSubmission <= 0) {
      toast.info("Nothing left to record. This bill is fully paid or waiting for approval");
      return;
    }

    if (amount > availableForSubmission) {
      toast.warning(`Receipt amount cannot exceed available amount ${money(availableForSubmission)}`);
      return;
    }

    if (!paymentForm.payment_mode) return toast.warning("Please select payment mode");

    if (
      paymentForm.payment_mode === "cheque" &&
      !String(paymentForm.cheque_no || "").trim()
    ) {
      return toast.warning("Please enter cheque number");
    }

    if (
      ["bank_transfer", "upi"].includes(paymentForm.payment_mode) &&
      !String(paymentForm.transaction_reference || "").trim()
    ) {
      return toast.warning("Please enter transaction reference");
    }

    if (!paymentForm.receipt_date) return toast.warning("Please select the payment date");

    const billId = activeBill.bill_id;

    try {
      const saved = await dispatch(
        addBillReceiptThunk({
          billId,
          payload: {
            ...paymentForm,
            received_amount: amount,
          },
        })
      ).unwrap();

      const savedStatus = String(saved?.data?.approval_status || "").toLowerCase();

      if (savedStatus === "approved") {
        // Backend already approved it (Superadmin payment). Nothing more to do.
        toast.success(saved?.message || "Payment recorded and approved.");
      } else if (!isSuperadmin) {
        toast.success("Payment recorded. Waiting for Superadmin approval.");
      } else {
        // Fallback only for an older backend that saves Superadmin payments as pending.
        // Superadmin: approve the receipt that was just created, so no
        // separate approval step is needed.
        setAutoApproving(true);
        const receiptId = await findNewReceiptId(saved, billId, amount);

        if (!receiptId) {
          toast.warning("Payment saved, but it could not be approved automatically. Please approve it in Receipt Approvals.");
        } else {
          try {
            await dispatch(approveReceiptThunk(receiptId)).unwrap();
            toast.success("Payment recorded and approved.");
          } catch (approveError) {
            if (/already approved/i.test(String(approveError || ""))) {
              toast.success("Payment recorded and approved.");
            } else {
              toast.warning(`Payment saved, but approval failed: ${approveError || "unknown error"}. Please approve it in Receipt Approvals.`);
            }
          }
        }
        setAutoApproving(false);
      }

      setPaymentModal(false);
      setActiveBill(null);
      loadBills();
    } catch (error) {
      setAutoApproving(false);
      toast.error(error || "Failed to record the payment");
    }
  };

  const openReceipts = async (bill) => {
    if (!bill) return;
    setActiveBill(bill);
    setReceiptData({ bill: null, receipts: [] });
    dispatch(clearReceiptHistory());
    setReceiptModal(true);

    try {
      const result = await dispatch(fetchBillReceiptsThunk(bill.bill_id)).unwrap();
      setReceiptData(result || { bill: null, receipts: [] });
    } catch (error) {
      toast.error(error || "Failed to load receipts");
    }
  };

  const closeReceipts = () => {
    setReceiptModal(false);
    setActiveBill(null);
    setReceiptData({ bill: null, receipts: [] });
    dispatch(clearReceiptHistory());
  };

  const openBillDetails = async (bill) => {
    if (!bill?.bill_id) return;

    dispatch(clearSelectedBill());
    setActiveBill(bill);
    setDetailsModal(true);

    try {
      await dispatch(
        fetchPanchayathBillByIdThunk(bill.bill_id)
      ).unwrap();
    } catch (error) {
      toast.error(error || "Failed to load bill details");
    }
  };

  const closeBillDetails = () => {
    setDetailsModal(false);
    setActiveBill(null);
    dispatch(clearSelectedBill());
  };

  const generateSubscriptions = async () => {
    setConfirmGenerateModal(false);
    try {
      const result = await dispatch(generateSubscriptionBillsThunk()).unwrap();
      const generated = Number(result?.data?.generated || 0);
      const skipped = Number(result?.data?.skipped || 0);
      const unsupported = Number(result?.data?.unsupported || 0);
      const checked = Number(result?.data?.checked_panchayaths || 0);

      if (checked === 0) toast.info("No eligible Panchayaths found for subscription billing");
      else if (generated > 0) toast.success(`${generated} subscription bill${generated === 1 ? "" : "s"} generated successfully`);
      else toast.info("No new subscription bills were generated");

      if (skipped > 0) toast.info(`${skipped} bill${skipped === 1 ? "" : "s"} skipped because already generated or not due`);
      if (unsupported > 0) toast.warning(`${unsupported} unsupported subscription${unsupported === 1 ? "" : "s"} skipped`);

      const next = { ...filters, page: 1 };
      setFilters(next);
      loadBills(next);
    } catch (error) {
      toast.error(error || "Failed to generate subscription bills");
    }
  };

  const loadGenerationHistory = (nextFilters = generationHistoryFilters) => {
    dispatch(
      fetchGenerationHistoryThunk({
        ...nextFilters,
        pan_id: hasActivePanchayath ? String(panIdActive) : nextFilters.pan_id,
      })
    );
  };

  const openGenerationHistory = () => {
    const next = {
      ...generationHistoryFilters,
      page: 1,
      search: String(generationHistoryFilters.search || "").trim(),
      pan_id: hasActivePanchayath ? String(panIdActive) : generationHistoryFilters.pan_id,
    };
    setGenerationHistoryFilters(next);
    setGenerationHistoryModal(true);
    loadGenerationHistory(next);
  };

  const searchGenerationHistory = () => {
    if (
      generationHistoryFilters.from_date &&
      generationHistoryFilters.to_date &&
      generationHistoryFilters.from_date > generationHistoryFilters.to_date
    ) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    const next = {
      ...generationHistoryFilters,
      page: 1,
      search: String(generationHistoryFilters.search || "").trim(),
      pan_id: hasActivePanchayath ? String(panIdActive) : generationHistoryFilters.pan_id,
    };
    setGenerationHistoryFilters(next);
    loadGenerationHistory(next);
  };

  const resetGenerationHistory = () => {
    const next = {
      page: 1,
      pageSize: 10,
      search: "",
      source: "",
      action: "",
      pan_id: hasActivePanchayath ? String(panIdActive) : "",
      from_date: "",
      to_date: "",
    };
    setGenerationHistoryFilters(next);
    loadGenerationHistory(next);
  };

  const changeGenerationHistoryPage = (page) => {
    const totalPages = Number(generationHistoryPagination?.totalPages || 0);
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;
    const next = { ...generationHistoryFilters, page };
    setGenerationHistoryFilters(next);
    loadGenerationHistory(next);
  };

  const latestCron = generationHistory?.latestCron || null;

  const PanchayathSelector = ({ value, onChange, allowAll = false, placeholder = "Select Panchayath" }) => {
    const [panSearch, setPanSearch] = useState("");

    if (hasActivePanchayath) {
      return (
        <div className="pb-pan-selected">
          <div className="pb-pan-selected-icon"><CheckCircle size={17} /></div>
          <div className="pb-pan-selected-content">
            <span>Active Panchayath</span>
            <strong>{activePanchayath?.pan_name || `Panchayath #${panIdActive}`}</strong>
          </div>
          <small>ID #{panIdActive}</small>
        </div>
      );
    }

    const selected = availablePanchayaths.find((item) => Number(item.pan_id) === Number(value)) || null;
    const searchText = String(panSearch || "").trim().toLowerCase();
    const filteredPanchayaths = searchText
      ? availablePanchayaths.filter((item) => {
          const name = String(item?.pan_name || "").toLowerCase();
          const id = String(item?.pan_id || "").toLowerCase();
          return name.includes(searchText) || id.includes(searchText);
        })
      : availablePanchayaths;

    return (
      <Dropdown className="pb-pan-dropdown" onToggle={(isOpen) => !isOpen && setPanSearch("")}>
        <Dropdown.Toggle className="pb-pan-dropdown-toggle" disabled={panchayathLoading}>
          <span className={selected ? "pb-pan-dropdown-value" : "pb-pan-dropdown-placeholder"}>
            {panchayathLoading
              ? "Loading Panchayaths..."
              : selected
              ? selected.pan_name
              : allowAll
              ? "All Panchayaths"
              : placeholder}
          </span>
          <span className="pb-pan-dropdown-arrow">▾</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="pb-pan-dropdown-menu">
          <div className="pb-pan-dropdown-search-wrap" onClick={(e) => e.stopPropagation()}>
            <Search size={15} className="pb-pan-dropdown-search-icon" />
            <Form.Control
              autoFocus
              type="text"
              className="pb-pan-dropdown-search"
              placeholder="Search Panchayath..."
              value={panSearch}
              onChange={(e) => setPanSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          <div className="pb-pan-dropdown-list">
          {allowAll && !searchText && (
            <Dropdown.Item
              active={!value}
              onClick={() => {
                onChange("");
                setPanSearch("");
              }}
            >
              <div className="pb-pan-dropdown-item">
                <span>All Panchayaths</span>
                {!value && <CheckCircle size={15} />}
              </div>
            </Dropdown.Item>
          )}

          {filteredPanchayaths.length > 0 ? (
            filteredPanchayaths.map((item) => {
              const selectedItem = Number(value) === Number(item.pan_id);
              return (
                <Dropdown.Item
                  key={item.pan_id}
                  active={selectedItem}
                  onClick={() => {
                    onChange(String(item.pan_id));
                    setPanSearch("");
                  }}
                >
                  <div className="pb-pan-dropdown-item">
                    <span>{item.pan_name}</span>
                    {selectedItem && <CheckCircle size={15} />}
                  </div>
                </Dropdown.Item>
              );
            })
          ) : (
            <div className="pb-pan-dropdown-empty">No Panchayath found</div>
          )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <div className="pb-page">
      <style>{`
        .pb-actions-with-text {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          min-width: 0;
        }

        .pb-actions .pb-action-btn.pb-action-text-btn {
          width: auto !important;
          min-width: max-content !important;
          height: 34px !important;
          min-height: 34px !important;
          padding: 6px 10px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 5px !important;
          border-radius: 6px !important;
          white-space: nowrap !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 1 !important;
        }

        .pb-actions .pb-action-btn.pb-action-text-btn svg {
          flex: 0 0 auto;
        }

        .pb-actions .pb-action-btn.pb-action-text-btn span {
          display: inline-block !important;
          white-space: nowrap;
        }

        .pb-bill-compact-table {
          width: 100% !important;
          min-width: 940px !important;
        }

        .pb-table-scroll,
        .pb-history-table-wrap {
          max-height: none !important;
          overflow-x: auto !important;
          overflow-y: visible !important;
        }

        .pb-actions-compact {
          min-width: max-content;
          flex-wrap: nowrap;
        }

        .pb-actions .pb-action-btn.details {
          color: #4d299f !important;
          border: 1px solid rgba(77, 41, 159, 0.28) !important;
        }

        .pb-details-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }

        .pb-details-grid > div {
          min-width: 0;
          padding: 12px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .pb-details-grid > div.highlight {
          background: #f7f4fc;
          border-color: rgba(77, 41, 159, 0.18);
        }

        .pb-details-grid span {
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
        }

        .pb-details-grid strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 700;
          overflow-wrap: anywhere;
        }

        .pb-details-remarks {
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          background: #fff;
        }

        .pb-details-remarks span {
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
        }

        .pb-details-remarks p {
          margin: 5px 0 0;
          color: #334155;
          font-size: 13px;
        }

        .pb-history-footer {
          min-height: 54px;
          padding-top: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .pb-history-table {
          width: 100% !important;
          min-width: 1100px !important;
        }

        .pb-history-table thead th {
          position: sticky !important;
          top: 0;
          z-index: 6;
          background: #f7f8fa !important;
          background-clip: padding-box;
        }

        .pb-pagination .page-link {
          font-family: Arial, Helvetica, sans-serif !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          line-height: 1 !important;
        }

        .pb-pagination .page-item.active .page-link {
          font-size: 13px !important;
        }

        .pb-history-table-wrap {
          width: 100%;
          max-width: 100%;
          max-height: 430px;
          overflow-x: auto !important;
          overflow-y: auto !important;
          scrollbar-width: thin;
          scrollbar-color: #b8c0cc #f2f4f7;
          scrollbar-gutter: stable;
          -webkit-overflow-scrolling: touch;
        }

        .pb-history-table-wrap::-webkit-scrollbar {
          width: 9px;
          height: 9px;
        }

        .pb-history-table-wrap::-webkit-scrollbar-track {
          background: #f2f4f7;
          border-radius: 999px;
        }

        .pb-history-table-wrap::-webkit-scrollbar-thumb {
          background: #b8c0cc;
          border-radius: 999px;
        }

        .pb-history-table-wrap::-webkit-scrollbar-thumb:hover {
          background: #98a2b3;
        }

        @media (max-width: 991px) {
          .pb-details-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .pb-history-footer {
            align-items: flex-start;
            flex-direction: column;
          }
        }

        @media (max-width: 575px) {
          .pb-details-grid {
            grid-template-columns: 1fr;
          }
        }

        .pb-actions-td,
        .pb-actions-th {
          min-width: 330px;
        }

        .pb-billing-settings-row {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #eef2f7;
          color: #64748b;
          font-size: 12px;
        }

        .pb-billing-settings-row strong {
          color: #0f172a;
          font-weight: 700;
        }

        .pb-tax-pill {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 4px 9px;
          border: 1px solid #dbe4f0;
          border-radius: 999px;
          background: #f8fafc;
          color: #334155;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .pb-tax-breakup {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #f8fafc;
        }

        .pb-tax-breakup span {
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
        }

        .pb-tax-breakup strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 700;
          text-align: right;
        }

        .pb-invoice-fixed-note {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #f8fafc;
        }

        .pb-invoice-fixed-note span {
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
        }

        .pb-invoice-fixed-note strong {
          color: #0f172a;
          font-size: 14px;
          font-weight: 700;
        }

        .pb-invoice-fixed-note small {
          margin-left: auto;
          color: #64748b;
          font-size: 11px;
        }

        .pb-table-amount-breakup {
          min-width: 190px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .pb-table-amount-breakup > div {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .pb-table-amount-breakup span {
          color: #98a2b3;
          font-size: 10.5px;
          font-weight: 500;
          white-space: nowrap;
        }

        .pb-table-amount-breakup strong {
          color: #475467;
          font-size: 11.5px;
          font-weight: 600;
          white-space: nowrap;
        }

        .pb-table-amount-breakup .total {
          margin-top: 3px;
          padding-top: 4px;
          border-top: 1px solid #eef1f4;
        }

        .pb-table-amount-breakup .total span,
        .pb-table-amount-breakup .total strong {
          color: #101828;
          font-weight: 700;
        }

        .pb-manual-tax-preview {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          background: #f8fafc;
        }

        .pb-manual-tax-preview > div {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .pb-manual-tax-preview span {
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
        }

        .pb-manual-tax-preview strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 700;
        }

        .pb-manual-tax-preview .total strong {
          font-size: 15px;
        }

        @media (max-width: 767px) {
          .pb-tax-breakup {
            flex-direction: column;
            align-items: flex-start;
          }

          .pb-tax-breakup strong {
            text-align: left;
          }

          .pb-manual-tax-preview {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .pb-manual-tax-preview .total {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 1200px) {
          .pb-actions-with-text {
            min-width: 0;
          }

          .pb-actions-td,
          .pb-actions-th {
            min-width: 310px;
          }

          .pb-actions .pb-action-btn.pb-action-text-btn {
            padding: 6px 8px !important;
            font-size: 11px !important;
          }
        }

        /* ===== Panchayath dropdown: search stays on top, list scrolls ===== */
        .pb-pan-dropdown .pb-pan-dropdown-menu {
          max-height: none !important;
          overflow: hidden !important;
          padding: 6px !important;
        }
        .pb-pan-dropdown .pb-pan-dropdown-list {
          max-height: 240px;
          overflow-y: auto;
          overflow-x: hidden;
          margin-top: 4px;
          padding-right: 2px;
          overscroll-behavior: contain;
        }

        /* Always-visible scrollbars (Edge hides "thin" ones until you scroll) */
        .pb-pan-dropdown .pb-pan-dropdown-list,
        .pb-history-dialog .pb-history-table-wrap {
          scrollbar-width: auto !important;
          scrollbar-color: auto !important;
        }
        .pb-pan-dropdown .pb-pan-dropdown-list::-webkit-scrollbar,
        .pb-history-dialog .pb-history-table-wrap::-webkit-scrollbar {
          width: 8px !important;
          height: 8px !important;
        }
        .pb-pan-dropdown .pb-pan-dropdown-list::-webkit-scrollbar-track,
        .pb-history-dialog .pb-history-table-wrap::-webkit-scrollbar-track {
          background: #f2f4f7 !important;
          border-radius: 999px;
        }
        .pb-pan-dropdown .pb-pan-dropdown-list::-webkit-scrollbar-thumb,
        .pb-history-dialog .pb-history-table-wrap::-webkit-scrollbar-thumb {
          background: #b8c0cc !important;
          border-radius: 999px;
        }
        .pb-pan-dropdown .pb-pan-dropdown-list::-webkit-scrollbar-thumb:hover,
        .pb-history-dialog .pb-history-table-wrap::-webkit-scrollbar-thumb:hover {
          background: #98a2b3 !important;
        }
        @supports not selector(::-webkit-scrollbar) {
          .pb-pan-dropdown .pb-pan-dropdown-list,
          .pb-history-dialog .pb-history-table-wrap {
            scrollbar-color: #b8c0cc #f2f4f7 !important;
          }
        }

        /* ===== Generation History popup: fits the screen, table scrolls ===== */
        .pb-history-dialog {
          max-width: min(1240px, calc(100vw - 32px)) !important;
          margin: 16px auto !important;
        }
        .pb-history-dialog .modal-content {
          max-height: calc(100vh - 32px);
        }
        .pb-history-dialog .pb-history-table-wrap {
          max-height: calc(100vh - 390px) !important;
          min-height: 220px;
          overflow-x: auto !important;
          overflow-y: auto !important;
        }
        .pb-history-dialog .pb-history-table thead th {
          position: sticky !important;
          top: 0;
          z-index: 6;
        }
        .pb-history-dialog .pb-history-reason {
          min-width: 260px;
          white-space: normal;
        }

        /* ===== Generate confirmation ===== */
        .pb-confirm-modal .pb-confirm-box {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }
        .pb-confirm-modal .pb-confirm-icon {
          flex: 0 0 42px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: #b54708;
          background: #fffaeb;
        }
        .pb-confirm-modal .pb-confirm-text p {
          margin: 2px 0 8px;
          color: #344054;
          font-size: 14px;
        }
        .pb-confirm-modal .pb-confirm-text ul {
          margin: 0;
          padding-left: 18px;
          color: #667085;
          font-size: 13px;
        }
        .pb-confirm-modal .pb-confirm-text li + li {
          margin-top: 4px;
        }

        /* ===== Bill Details popup: compact sections ===== */
        .pb-bd { display: flex; flex-direction: column; gap: 10px; }
        .pb-bd-muted { color: #667085; font-size: 12px; }
        .pb-bd-summary {
          display: flex; justify-content: space-between; align-items: center; gap: 12px;
          padding: 10px 14px; border-radius: 10px;
          background: #f7f5fd; border: 1px solid #e7e0f7;
        }
        .pb-bd-summary-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .pb-bd-summary-side { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .pb-bd-pan { font-size: 15px; color: #101828; font-weight: 700; }
        .pb-bd-id { font-size: 12px; font-weight: 500; color: #98a2b3; margin-left: 4px; }
        .pb-bd-total { font-size: 18px; color: #4d299f; font-weight: 700; line-height: 1; }
        .pb-bd-two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .pb-bd-card { border: 1px solid #eaecf0; border-radius: 10px; padding: 10px 14px; background: #fff; }
        .pb-bd-title {
          margin: 0 0 6px; font-size: 11px; font-weight: 700; color: #667085;
          text-transform: uppercase; letter-spacing: .06em;
        }
        .pb-bd-line {
          display: flex; justify-content: space-between; align-items: baseline; gap: 8px;
          padding: 3px 0; font-size: 13px; color: #475467;
        }
        .pb-bd-line strong { color: #101828; font-weight: 600; white-space: nowrap; }
        .pb-bd-line.total { margin-top: 3px; padding-top: 6px; border-top: 1px dashed #d0d5dd; }
        .pb-bd-line.total span, .pb-bd-line.total strong { font-weight: 700; color: #4d299f; }
        .pb-bd-line.green strong { color: #067647; }
        .pb-bd-line.total.green span, .pb-bd-line.total.green strong { color: #067647; }
        .pb-bd-line.total.orange span, .pb-bd-line.total.orange strong { color: #b54708; }
        .pb-bd-progress { height: 6px; margin-top: 8px; border-radius: 999px; background: #eaecf0; overflow: hidden; }
        .pb-bd-progress > div { height: 100%; border-radius: 999px; background: #17b26a; }
        .pb-bd-progress-text { margin-top: 4px; font-size: 11.5px; font-weight: 600; color: #475467; }
        .pb-bd-inv { display: grid; grid-template-columns: 1.6fr 1fr 1fr .8fr; gap: 6px 14px; }
        .pb-bd-inv > div { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .pb-bd-inv span { color: #98a2b3; font-size: 11px; font-weight: 600; }
        .pb-bd-inv strong { color: #101828; font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
        .pb-bd-inv .pb-invoice-file-link { font-size: 13px; }
        .pb-bd-empty {
          padding: 7px 10px; border-radius: 7px; font-size: 12.5px;
          color: #b54708; background: #fffaeb; border: 1px solid #fedf89;
        }
        .pb-bd-remarks { margin: 0; font-size: 13px; color: #344054; white-space: pre-wrap; }
        @media (max-width: 767px) {
          .pb-bd-two { grid-template-columns: 1fr; }
          .pb-bd-inv { grid-template-columns: 1fr 1fr; }
          .pb-bd-summary { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
      <div className="pb-header">
        <div className="pb-header-left"><h3 className="pb-title">Panchayath Billing</h3></div>
        <div className="pb-header-actions">
          <Button className="pb-btn pb-btn-light" onClick={openGenerationHistory}>
            <Activity size={16} /> Generation History
          </Button>
          <Button className="pb-btn pb-btn-outline" disabled={generating} onClick={() => setConfirmGenerateModal(true)}>
            {generating ? <><Spinner animation="border" size="sm" /> Generating...</> : <><RefreshCcw size={16} /> Generate Subscriptions</>}
          </Button>
          <Button className="pb-btn pb-btn-primary" onClick={openManualBill}>
            <Plus size={17} /> Add Bill
          </Button>
        </div>
      </div>

      <Card className="pb-cron-card">
        <Card.Body>
          <div className="pb-cron-head">
            <div>
              <div className="pb-cron-title"><Activity size={18} /> Last Automatic Billing Run</div>
              <span className="pb-cron-subtitle">
                Subscription billing scheduler time: {formatCronTime(billingSettings.subscription_cron_time)} IST.
              </span>
            </div>
            <Button className="pb-btn pb-btn-light" onClick={openGenerationHistory}>View History</Button>
          </div>

          <div className="pb-billing-settings-row">
            <span>GST: <strong>{billingSettings.gst_percentage}%</strong></span>
            <span>Cron: <strong>{billingSettings.subscription_cron_time}</strong></span>
            <span>Invoice Prefix: <strong>{billingSettings.invoice_prefix}</strong></span>
          </div>

          {generationHistoryLoading && !latestCron ? (
            <div className="pb-cron-loading"><Spinner animation="border" size="sm" /> Loading latest run...</div>
          ) : latestCron ? (
            <Row className="g-2 mt-2">
              <Col xl={3} md={6}><div className="pb-cron-stat"><span>Run Time</span><strong>{latestCron.run_time ? new Date(latestCron.run_time).toLocaleString("en-IN") : "-"}</strong></div></Col>
              <Col xl={2} md={3} xs={6}><div className="pb-cron-stat"><span>Checked</span><strong>{latestCron.checked || 0}</strong></div></Col>
              <Col xl={2} md={3} xs={6}><div className="pb-cron-stat success"><span>Generated</span><strong>{latestCron.generated || 0}</strong></div></Col>
              <Col xl={2} md={3} xs={6}><div className="pb-cron-stat warning"><span>Skipped</span><strong>{latestCron.skipped || 0}</strong></div></Col>
              <Col xl={1} md={3} xs={6}><div className="pb-cron-stat"><span>Unsupported</span><strong>{latestCron.unsupported || 0}</strong></div></Col>
              <Col xl={2} md={3} xs={6}><div className="pb-cron-stat danger"><span>Failed</span><strong>{latestCron.failed || 0}</strong></div></Col>
            </Row>
          ) : (
            <div className="pb-cron-empty">No automatic cron execution has been recorded yet.</div>
          )}
        </Card.Body>
      </Card>

      {isPendingSubscriptionFilter && (
        <div className="pb-active-filter-notice">
          <div className="pb-active-filter-left">
            <div className="pb-active-filter-icon"><AlertCircle size={18} /></div>
            <div><strong>Pending Subscription Bills</strong><span>Showing subscription bills currently pending for payment.</span></div>
          </div>
          <Button className="pb-btn pb-btn-light" onClick={handleReset}><X size={15} /> Clear Filter</Button>
        </div>
      )}

      <Row className="g-3 pb-summary-row">
        <Col xl={3} md={6}><div className="pb-summary-card"><div className="pb-summary-icon pb-icon-blue"><FileText size={21} /></div><div className="pb-summary-content"><span className="pb-summary-label">Total Bills</span><strong className="pb-summary-value">{pagination.total || 0}</strong><small>Billing records</small></div></div></Col>
        <Col xl={3} md={6}><div className="pb-summary-card"><div className="pb-summary-icon pb-icon-purple"><TrendingUp size={21} /></div><div className="pb-summary-content"><span className="pb-summary-label">Billing Amount</span><strong className="pb-summary-value">{money(summary.totalBill)}</strong><small>All filtered records</small></div></div></Col>
        <Col xl={3} md={6}><div className="pb-summary-card"><div className="pb-summary-icon pb-icon-green"><CheckCircle size={21} /></div><div className="pb-summary-content"><span className="pb-summary-label">Approved Received</span><strong className="pb-summary-value">{money(summary.totalReceived)}</strong><small>Approved receipts only</small></div></div></Col>
        <Col xl={3} md={6}><div className="pb-summary-card"><div className="pb-summary-icon pb-icon-orange"><AlertCircle size={21} /></div><div className="pb-summary-content"><span className="pb-summary-label">Outstanding Balance</span><strong className="pb-summary-value">{money(summary.totalBalance)}</strong><small>Pending collection</small></div></div></Col>
      </Row>

      <Card className="pb-filter-card">
        <Card.Body>
          <div className="pb-section-head"><div><h5>Billing Records</h5><span>Search and filter Panchayath billing details</span></div></div>
          <Row className="g-3 pb-filter-grid">
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>Search</Form.Label><InputGroup className="pb-search-group"><InputGroup.Text><Search size={16} /></InputGroup.Text><Form.Control className="pb-control" placeholder="Panchayath / Invoice / Bill ID" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && handleSearch()} /></InputGroup></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>Panchayath</Form.Label><PanchayathSelector value={filters.pan_id} allowAll onChange={(value) => setFilters((prev) => ({ ...prev, pan_id: value }))} /></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>Category</Form.Label><Form.Select className="pb-control" value={filters.category} onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}><option value="">All Categories</option>{(lookups.categories || []).map((item) => <option key={item} value={item}>{labelize(item)}</option>)}</Form.Select></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>Status</Form.Label><Form.Select className="pb-control" value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}><option value="">All Status</option>{(lookups.statuses || []).map((item) => <option key={item} value={item}>{labelize(item)}</option>)}</Form.Select></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>Generated Type</Form.Label><Form.Select className="pb-control" value={filters.generated_type} onChange={(e) => setFilters((prev) => ({ ...prev, generated_type: e.target.value }))}><option value="">All Types</option><option value="automatic">Automatic</option><option value="manual">Manual</option></Form.Select></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>From Date</Form.Label><Form.Control className="pb-control" type="date" value={filters.from_date} onChange={(e) => setFilters((prev) => ({ ...prev, from_date: e.target.value }))} /></Form.Group></Col>
            <Col className="pb-filter-col" xl={2} lg={4} md={6}><Form.Group><Form.Label>To Date</Form.Label><Form.Control className="pb-control" type="date" min={filters.from_date || undefined} value={filters.to_date} onChange={(e) => setFilters((prev) => ({ ...prev, to_date: e.target.value }))} /></Form.Group></Col>
            <Col xl={2} lg={4} md={6} className="pb-filter-col pb-filter-actions"><Button className="pb-btn pb-btn-primary" onClick={handleSearch}><Search size={15} /> Search</Button><Button className="pb-btn pb-btn-light" onClick={handleReset}><RefreshCcw size={15} /> Reset</Button></Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="pb-table-card">
        <div className="pb-table-title">
          <div>
            <h5>Bill List</h5>
            <span>
              {pagination.total || 0} billing record
              {Number(pagination.total || 0) === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <div className="pb-table-scroll">
          <Table hover className="pb-table pb-bill-compact-table align-middle">
            <thead>
              <tr>
                <th>Sl. No.</th>
                <th>Bill Date</th>
                <th>Panchayath</th>
                <th>Category</th>
                <th>Bill Amount</th>
                <th>Status</th>
                <th className="pb-actions-th">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="pb-loading-cell">
                    <Spinner animation="border" size="sm" /> Loading billing records...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="pb-empty-cell">
                    <div className="pb-empty-icon">
                      <FileText size={25} />
                    </div>
                    <strong>No billing records found</strong>
                    <span>
                      {isPendingSubscriptionFilter
                        ? "No pending subscription bills found."
                        : "Create a manual bill or generate subscription bills."}
                    </span>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.bill_id}>
                    <td>
                      {(Number(pagination.page || 1) - 1) *
                        Number(pagination.pageSize || 10) +
                        index +
                        1}
                    </td>

                    <td>{row.bill_date || "-"}</td>

                    <td>
                      <div className="pb-pan-name">{row.pan_name || "-"}</div>
                      <small className="pb-pan-id">ID #{row.pan_id}</small>
                    </td>

                    <td>
                      <span className="pb-category">
                        {labelize(row.bill_category)}
                      </span>
                    </td>

                    <td>
                      <div className="pb-table-amount-breakup">
                        <div>
                          <span>Base Amount</span>
                          <strong>
                            {money(
                              Number(row.taxable_amount || 0) > 0
                                ? row.taxable_amount
                                : row.bill_amount
                            )}
                          </strong>
                        </div>

                        <div>
                          <span>Tax</span>
                          <strong>
                            {isGstBill(row)
                              ? `GST ${Number(row.tax_percentage || 0)}%`
                              : row.tax_type
                              ? labelize(row.tax_type)
                              : "No Tax"}
                          </strong>
                        </div>

                        <div>
                          <span>GST Amount</span>
                          <strong>{money(row.tax_amount || 0)}</strong>
                        </div>

                        <div className="total">
                          <span>Final Bill Amount</span>
                          <strong>{money(row.bill_amount)}</strong>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className={`pb-status-pill pb-status-${row.status}`}>
                        {labelize(row.status)}
                      </span>
                    </td>

                    <td className="pb-actions-td">
                      <div className="pb-actions pb-actions-compact">
                        <Button
                          className="pb-action-btn pb-action-text-btn details"
                          title="View Bill Details"
                          onClick={() => openBillDetails(row)}
                        >
                          <Eye size={15} />
                          <span>Details</span>
                        </Button>

                        {row.status !== "cancelled" && (
                          <Button
                            className="pb-action-btn pb-action-text-btn invoice"
                            title={row.invoice_number ? "Edit Invoice" : "Add Invoice"}
                            onClick={() => openInvoice(row)}
                          >
                            <FileText size={15} />
                            <span>
                              {row.invoice_number ? "Edit Invoice" : "Add Invoice"}
                            </span>
                          </Button>
                        )}

                        {row.invoice_number &&
                          row.status !== "cleared" &&
                          row.status !== "cancelled" && (
                            <Button
                              className="pb-action-btn pb-action-text-btn payment"
                              title="Enter a payment received for this bill"
                              onClick={() => openPayment(row)}
                            >
                              <DollarSign size={15} />
                              <span>Record Payment</span>
                            </Button>
                          )}

                        {row.invoice_number && (
                          <Button
                            className="pb-action-btn pb-action-text-btn receipts"
                            title="See receipts for this bill and their approval status"
                            onClick={() => openReceipts(row)}
                          >
                            <CreditCard size={15} />
                            <span>Receipt History</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <div className="pb-table-footer">
          <div className="pb-record-count">
            Showing page <strong>{pagination.page || 1}</strong> of{" "}
            <strong>{pagination.totalPages || 1}</strong>
            <span>•</span>
            Total <strong>{pagination.total || 0}</strong> records
          </div>

          {pagination.totalPages > 1 && (
            <Pagination className="pb-pagination mb-0">
              <Pagination.Item
                disabled={pagination.page <= 1}
                onClick={() => changePage(1)}
                aria-label="First page"
                title="First page"
              >
                «
              </Pagination.Item>

              <Pagination.Item
                disabled={pagination.page <= 1}
                onClick={() => changePage(pagination.page - 1)}
                aria-label="Previous page"
                title="Previous page"
              >
                ‹
              </Pagination.Item>

              <Pagination.Item active>
                {pagination.page}
              </Pagination.Item>

              <Pagination.Item
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => changePage(pagination.page + 1)}
                aria-label="Next page"
                title="Next page"
              >
                ›
              </Pagination.Item>

              <Pagination.Item
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => changePage(pagination.totalPages)}
                aria-label="Last page"
                title="Last page"
              >
                »
              </Pagination.Item>
            </Pagination>
          )}
        </div>
      </Card>

      <Modal
        show={detailsModal}
        onHide={closeBillDetails}
        size="lg"
        centered
        className="pb-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Bill Details</Modal.Title>
            <div className="pb-modal-subtitle">
              Bill, amount, invoice and payment details
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {detailLoading ? (
            <div className="pb-modal-loading">
              <Spinner />
              <span>Loading bill details...</span>
            </div>
          ) : selectedBill ? (
            <>
              {(() => {
                const b = selectedBill;
                const baseAmount = Number(b.taxable_amount || 0) > 0 ? b.taxable_amount : b.bill_amount;
                const hasTax = Number(b.tax_amount || 0) > 0;
                const taxLabel = isGstBill(b)
                  ? `GST ${Number(b.tax_percentage || 0)}%`
                  : b.tax_type
                  ? labelize(b.tax_type)
                  : "No Tax";
                const hasInvoice = Boolean(b.invoice_number);
                const payable = Number(hasInvoice && b.invoice_amount != null ? b.invoice_amount : b.bill_amount) || 0;
                const received = Number(b.total_received || 0);
                const balance = Number(b.balance_amount || 0);
                const paidPercent = payable > 0 ? Math.min(100, Math.round((received / payable) * 100)) : 0;
                const meta = [
                  labelize(b.bill_category),
                  labelize(b.generated_type),
                  b.billing_period ? `Period ${b.billing_period}` : null,
                  b.bill_date ? `Billed ${b.bill_date}` : null,
                ].filter(Boolean);

                return (
                  <div className="pb-bd">
                    {/* Summary: who, what, how much, status */}
                    <div className="pb-bd-summary">
                      <div className="pb-bd-summary-main">
                        <div className="pb-bd-pan">
                          {b.pan_name || "-"} <span className="pb-bd-id">#{b.bill_id}</span>
                        </div>
                        <div className="pb-bd-muted">{meta.join(" · ")}</div>
                      </div>
                      <div className="pb-bd-summary-side">
                        <span className={`pb-status-pill pb-status-${b.status}`}>{labelize(b.status)}</span>
                        <strong className="pb-bd-total">{money(b.bill_amount)}</strong>
                      </div>
                    </div>

                    {/* Amount + Payment side by side */}
                    <div className="pb-bd-two">
                      <section className="pb-bd-card">
                        <div className="pb-bd-title">Amount</div>
                        <div className="pb-bd-line"><span>Base Amount</span><strong>{money(baseAmount)}</strong></div>
                        <div className="pb-bd-line">
                          <span>{hasTax ? `+ ${taxLabel}` : `Tax (${taxLabel})`}</span>
                          <strong>{money(b.tax_amount || 0)}</strong>
                        </div>
                        <div className="pb-bd-line total"><span>Final Bill Amount</span><strong>{money(b.bill_amount)}</strong></div>
                      </section>

                      <section className="pb-bd-card">
                        <div className="pb-bd-title">Payment</div>
                        <div className="pb-bd-line"><span>Amount to Pay</span><strong>{money(payable)}</strong></div>
                        <div className="pb-bd-line green"><span>Received (approved)</span><strong>{money(received)}</strong></div>
                        <div className={`pb-bd-line total ${balance > 0 ? "orange" : "green"}`}>
                          <span>Balance</span><strong>{money(balance)}</strong>
                        </div>
                        <div className="pb-bd-progress" aria-label={`${paidPercent}% paid`}>
                          <div style={{ width: `${paidPercent}%` }} />
                        </div>
                        <div className="pb-bd-progress-text">
                          {balance <= 0 && received > 0 ? "Fully paid" : `${paidPercent}% paid`}
                        </div>
                      </section>
                    </div>

                    {/* Invoice: one compact row */}
                    <section className="pb-bd-card">
                      <div className="pb-bd-title">Invoice</div>
                      {hasInvoice ? (
                        <div className="pb-bd-inv">
                          <div><span>No.</span><strong>{b.invoice_number}</strong></div>
                          <div><span>Date</span><strong>{b.invoice_date || "-"}</strong></div>
                          <div><span>Amount</span><strong>{b.invoice_amount != null ? money(b.invoice_amount) : "-"}</strong></div>
                          <div>
                            <span>File</span>
                            {b.invoice_file ? (
                              <a
                                className="pb-invoice-file-link"
                                href={getInvoiceFileUrl(b.invoice_file)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FileText size={13} /> View
                              </a>
                            ) : (
                              <strong className="pb-bd-muted">Not uploaded</strong>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="pb-bd-empty">Not added yet. Add the invoice to start recording payments.</div>
                      )}
                    </section>

                    {b.remarks && (
                      <section className="pb-bd-card">
                        <div className="pb-bd-title">Remarks</div>
                        <p className="pb-bd-remarks">{b.remarks}</p>
                      </section>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="pb-empty-cell">
              <strong>Bill details not available</strong>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button className="pb-btn pb-btn-light" onClick={closeBillDetails}>
            <X size={15} /> Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={manualModal} onHide={() => setManualModal(false)} centered className="pb-modal">
        <Modal.Header closeButton><div><Modal.Title>Add Manual Bill</Modal.Title><div className="pb-modal-subtitle">Installation, training, printer or other charges</div></div></Modal.Header>
        <Modal.Body><Row className="g-3">
          <Col xs={12}><Form.Group><Form.Label>Panchayath <span className="pb-required">*</span></Form.Label><PanchayathSelector value={manualForm.pan_id} onChange={(value) => setManualForm((prev) => ({ ...prev, pan_id: value }))} /></Form.Group></Col>
          <Col md={6}><Form.Group><Form.Label>Category <span className="pb-required">*</span></Form.Label><Form.Select className="pb-control" value={manualForm.bill_category} onChange={(e) => setManualForm((prev) => ({ ...prev, bill_category: e.target.value }))}>{(lookups.manual_categories || []).map((item) => <option key={item} value={item}>{labelize(item)}</option>)}</Form.Select></Form.Group></Col>
          <Col md={6}><Form.Group><Form.Label>Bill Date <span className="pb-required">*</span></Form.Label><Form.Control className="pb-control" type="date" value={manualForm.bill_date} onChange={(e) => setManualForm((prev) => ({ ...prev, bill_date: e.target.value }))} /></Form.Group></Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>
                Base Bill Amount <span className="pb-required">*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text className="pb-currency-addon">₹</InputGroup.Text>
                <Form.Control
                  className="pb-control pb-amount-control"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Enter amount before GST"
                  value={manualForm.bill_amount}
                  onChange={(e) =>
                    setManualForm((prev) => ({
                      ...prev,
                      bill_amount: e.target.value,
                    }))
                  }
                />
              </InputGroup>
              <div className="text-muted small mt-1">
                Enter the amount before tax. Tax is applied automatically from the selected category's Billing Configuration.
              </div>
            </Form.Group>
          </Col>

          {Number(manualForm.bill_amount || 0) > 0 && (
            <Col xs={12}>
              <div className="pb-manual-tax-preview">
                <div>
                  <span>Base Amount</span>
                  <strong>{money(manualTaxPreview.baseAmount)}</strong>
                </div>

                <div>
                  <span>Tax Type</span>
                  <strong>
                    {manualTaxPreview.taxType || "Not configured"}
                  </strong>
                </div>

                <div>
                  <span>GST</span>
                  <strong>
                    {!manualTaxPreview.isGst
                      ? "0%"
                      : manualTaxPreview.taxMode === "amount"
                      ? `Fixed ${money(manualTaxPreview.taxValue)}`
                      : `${manualTaxPreview.gstPercentage}%`}
                  </strong>
                </div>

                <div>
                  <span>GST Amount</span>
                  <strong>{money(manualTaxPreview.taxAmount)}</strong>
                </div>

                <div className="total">
                  <span>Final Bill Amount</span>
                  <strong>{money(manualTaxPreview.finalAmount)}</strong>
                </div>
              </div>
            </Col>
          )}

          <Col xs={12}><Form.Group><Form.Label>Remarks</Form.Label><Form.Control className="pb-textarea" as="textarea" rows={3} placeholder="Optional remarks" value={manualForm.remarks} onChange={(e) => setManualForm((prev) => ({ ...prev, remarks: e.target.value }))} /></Form.Group></Col>
        </Row></Modal.Body>
        <Modal.Footer><Button className="pb-btn pb-btn-light" disabled={savingManualBill} onClick={() => setManualModal(false)}>Cancel</Button><Button className="pb-btn pb-btn-primary" disabled={savingManualBill} onClick={submitManualBill}>{savingManualBill ? <><Spinner size="sm" /> Saving...</> : <><Plus size={16} /> Create Bill</>}</Button></Modal.Footer>
      </Modal>

      <Modal show={invoiceModal} onHide={() => { setInvoiceModal(false); setActiveBill(null); }} centered className="pb-modal">
        <Modal.Header closeButton><div><Modal.Title>Invoice Details</Modal.Title><div className="pb-modal-subtitle">Add or update invoice information</div></div></Modal.Header>
        <Modal.Body>
          {activeBill && (
            <>
              <div className="pb-selected-bill">
                <div><span>Panchayath</span><strong>{activeBill.pan_name || "-"}</strong></div>
                <div><span>Category</span><strong>{labelize(activeBill.bill_category)}</strong></div>
                <div>
                  <span>Base Amount</span>
                  <strong>{money(Number(activeBill.taxable_amount || 0) > 0 ? activeBill.taxable_amount : activeBill.bill_amount)}</strong>
                </div>
                <div>
                  <span>GST</span>
                  <strong>{isGstBill(activeBill) ? `${Number(activeBill.tax_percentage || 0)}%` : "No Tax"}</strong>
                </div>
                {isGstBill(activeBill) && (
                  <div><span>GST Amount</span><strong>{money(activeBill.tax_amount)}</strong></div>
                )}
                <div><span>Final Bill Amount</span><strong>{money(activeBill.bill_amount)}</strong></div>
                {Number(activeBill.total_received || 0) > 0 && (
                  <div><span>Already Approved</span><strong className="pb-green-text">{money(activeBill.total_received)}</strong></div>
                )}
              </div>

              {isGstBill(activeBill) && (
                <div className="pb-tax-breakup mb-3">
                  <span>GST Calculation</span>
                  <strong>
                    {money(activeBill.taxable_amount)} + {Number(activeBill.tax_percentage || 0)}% GST ({money(activeBill.tax_amount)}) = {money(activeBill.bill_amount)}
                  </strong>
                </div>
              )}

              <div className="pb-invoice-fixed-note mb-3">
                <span>Invoice Amount</span>
                <strong>{money(activeBill.bill_amount)}</strong>
                <small>Fixed to Final Bill Amount</small>
              </div>
            </>
          )}
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  className="pb-control"
                  placeholder={`Leave blank to auto-generate (${billingSettings.invoice_prefix}-...)`}
                  value={invoiceForm.invoice_number}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      invoice_number: e.target.value,
                    }))
                  }
                />
                <div className="text-muted small mt-1">
                  Leave blank to auto-generate using prefix <strong>{billingSettings.invoice_prefix}</strong>.
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Invoice Amount <span className="pb-required">*</span>
                </Form.Label>

                <InputGroup>
                  <InputGroup.Text className="pb-currency-addon">₹</InputGroup.Text>

                  <Form.Control
                    className="pb-control pb-amount-control"
                    type="number"
                    value={activeBill?.bill_amount ?? ""}
                    readOnly
                    disabled
                  />
                </InputGroup>

                <div className="text-muted small mt-1">
                  Invoice Amount is fixed to the Final Bill Amount, including GST.
                </div>
              </Form.Group>
            </Col>
            <Col md={6}><Form.Group><Form.Label>Invoice Date <span className="pb-required">*</span></Form.Label><Form.Control className="pb-control" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, invoice_date: e.target.value }))} /></Form.Group></Col>
            <Col xs={12}><Form.Group><Form.Label>Invoice File</Form.Label><Form.Control className="pb-control" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceForm((prev) => ({ ...prev, invoice_file: e.target.files?.[0] || null }))} /><div className="text-muted small mt-1">Only one invoice file per bill. PDF, JPG, JPEG or PNG. Maximum 10 MB.</div>{activeBill?.invoice_file && !invoiceForm.invoice_file && <div className="mt-2"><a className="pb-invoice-file-link" href={getInvoiceFileUrl(activeBill.invoice_file)} target="_blank" rel="noreferrer"><FileText size={15} /> View Existing Invoice</a></div>}{invoiceForm.invoice_file && <div className="pb-selected-file mt-2"><Upload size={14} /><span>{invoiceForm.invoice_file.name}</span></div>}</Form.Group></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer><Button className="pb-btn pb-btn-light" disabled={savingInvoice} onClick={() => { setInvoiceModal(false); setActiveBill(null); }}>Cancel</Button><Button className="pb-btn pb-btn-primary" disabled={savingInvoice} onClick={submitInvoice}>{savingInvoice ? <><Spinner size="sm" /> Saving...</> : <><FileText size={16} /> Save Invoice</>}</Button></Modal.Footer>
      </Modal>

      <Modal show={paymentModal} onHide={() => { setPaymentModal(false); setActiveBill(null); }} centered size="lg" className="pb-modal">
        <Modal.Header closeButton><div><Modal.Title>Record Payment</Modal.Title><div className="pb-modal-subtitle">{isSuperadmin ? "Enter the payment received. As Superadmin, it is approved immediately." : "Enter the payment received. It will show as Pending Approval until Superadmin approves it."}</div></div></Modal.Header>
        <Modal.Body>
          {activeBill && (
            <>
              {isGstBill(activeBill) && (
                <div className="pb-tax-breakup mb-3">
                  <span>Bill Tax Breakup</span>
                  <strong>
                    Base {money(activeBill.taxable_amount)} + GST {Number(activeBill.tax_percentage || 0)}% ({money(activeBill.tax_amount)}) = {money(activeBill.bill_amount)}
                  </strong>
                </div>
              )}

              <Row className="g-2 mb-4">
                <Col md={3}><div className="pb-mini-card"><span>Invoice</span><strong>{activeBill.invoice_number || "-"}</strong></div></Col>
                <Col md={3}><div className="pb-mini-card"><span>Invoice Amount</span><strong>{money(activeBill.invoice_amount)}</strong></div></Col>
                <Col md={3}><div className="pb-mini-card received"><span>Approved</span><strong>{money(activeBill.total_received)}</strong></div></Col>
                <Col md={3}><div className="pb-mini-card balance"><span>Available</span><strong>{money(activeBill.available_for_submission ?? activeBill.balance_amount)}</strong></div></Col>
                {Number(activeBill.pending_received || 0) > 0 && <Col xs={12}><div className="pb-auto-receipt-number">Pending Approval: <strong>{money(activeBill.pending_received)}</strong></div></Col>}
              </Row>
            </>
          )}

          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Amount Paid <span className="pb-required">*</span></Form.Label><InputGroup><InputGroup.Text className="pb-currency-addon">₹</InputGroup.Text><Form.Control className="pb-control pb-amount-control" type="number" min="0.01" step="0.01" max={Number(activeBill?.available_for_submission ?? activeBill?.balance_amount ?? 0) || undefined} placeholder="Enter amount" value={paymentForm.received_amount} onChange={(e) => setPaymentForm((prev) => ({ ...prev, received_amount: e.target.value }))} /></InputGroup></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Payment Mode <span className="pb-required">*</span></Form.Label><Form.Select className="pb-control" value={paymentForm.payment_mode} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_mode: e.target.value, cheque_no: "", cheque_date: "", bank_name: "", transaction_reference: "" }))}>{(lookups.payment_modes || []).map((item) => <option key={item} value={item}>{labelize(item)}</option>)}</Form.Select></Form.Group></Col>

            {paymentForm.payment_mode === "cheque" && <>
              <Col md={4}><Form.Group><Form.Label>Cheque Number <span className="pb-required">*</span></Form.Label><Form.Control className="pb-control" placeholder="Cheque number" value={paymentForm.cheque_no} onChange={(e) => setPaymentForm((prev) => ({ ...prev, cheque_no: e.target.value }))} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Cheque Date</Form.Label><Form.Control className="pb-control" type="date" value={paymentForm.cheque_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, cheque_date: e.target.value }))} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Bank Name</Form.Label><Form.Control className="pb-control" placeholder="Bank name" value={paymentForm.bank_name} onChange={(e) => setPaymentForm((prev) => ({ ...prev, bank_name: e.target.value }))} /></Form.Group></Col>
            </>}

            {["bank_transfer", "upi"].includes(paymentForm.payment_mode) && <Col md={6}><Form.Group><Form.Label>Transaction / UTR Number <span className="pb-required">*</span></Form.Label><Form.Control className="pb-control" placeholder="Transaction / UTR / UPI reference" value={paymentForm.transaction_reference} onChange={(e) => setPaymentForm((prev) => ({ ...prev, transaction_reference: e.target.value }))} /></Form.Group></Col>}

            <Col md={6}><Form.Group><Form.Label>Receipt Number</Form.Label><div className="pb-auto-receipt-number">Receipt number will be generated automatically after submission</div></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Payment Date <span className="pb-required">*</span></Form.Label><Form.Control className="pb-control" type="date" value={paymentForm.receipt_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, receipt_date: e.target.value }))} /></Form.Group></Col>
            <Col xs={12}><Form.Group><Form.Label>Remarks</Form.Label><Form.Control className="pb-textarea" as="textarea" rows={3} placeholder="Optional remarks" value={paymentForm.remarks} onChange={(e) => setPaymentForm((prev) => ({ ...prev, remarks: e.target.value }))} /></Form.Group></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer><Button className="pb-btn pb-btn-light" disabled={savingReceipt || autoApproving} onClick={() => { setPaymentModal(false); setActiveBill(null); }}>Cancel</Button><Button className="pb-btn pb-btn-success" disabled={savingReceipt || autoApproving} onClick={submitPayment}>{savingReceipt || autoApproving ? <><Spinner size="sm" /> {autoApproving ? "Approving..." : "Submitting..."}</> : <><DollarSign size={16} /> {isSuperadmin ? "Save & Approve" : "Submit for Approval"}</>}</Button></Modal.Footer>
      </Modal>


      <Modal
        show={confirmGenerateModal}
        onHide={() => setConfirmGenerateModal(false)}
        centered
        className="pb-modal pb-confirm-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Generate subscription bills?</Modal.Title>
            <div className="pb-modal-subtitle">Please confirm before continuing</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="pb-confirm-box">
            <div className="pb-confirm-icon"><AlertCircle size={22} /></div>
            <div className="pb-confirm-text">
              <p>
                This will check every Panchayath with an active subscription and
                create bills for those that are due now.
              </p>
              <ul>
                <li>Panchayaths already billed for this period are skipped.</li>
                <li>New bills will appear in the Bill List as <strong>Pending</strong>.</li>
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="pb-btn pb-btn-light" onClick={() => setConfirmGenerateModal(false)}>
            Cancel
          </Button>
          <Button className="pb-btn pb-btn-primary" disabled={generating} onClick={generateSubscriptions}>
            <RefreshCcw size={16} /> Yes, Generate
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={generationHistoryModal}
        onHide={() => setGenerationHistoryModal(false)}
        size="xl"
        centered
        scrollable
        className="pb-modal"
        dialogClassName="pb-history-dialog"
      >
        <Modal.Header closeButton><div><Modal.Title>Subscription Generation History</Modal.Title><div className="pb-modal-subtitle">View automatic cron and manual generation results, including skip reasons.</div></div></Modal.Header>
        <Modal.Body>
          <Row className="g-3 mb-3">
            <Col xl={3} lg={4} md={6}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup className="pb-search-group">
                  <InputGroup.Text><Search size={16} /></InputGroup.Text>
                  <Form.Control
                    className="pb-control"
                    placeholder="Panchayath / Period / Reason"
                    value={generationHistoryFilters.search}
                    onChange={(e) =>
                      setGenerationHistoryFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" && searchGenerationHistory()
                    }
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col xl={2} lg={4} md={6}>
              <Form.Group>
                <Form.Label>Source</Form.Label>
                <Form.Select
                  className="pb-control"
                  value={generationHistoryFilters.source}
                  onChange={(e) =>
                    setGenerationHistoryFilters((prev) => ({
                      ...prev,
                      source: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="cron">Cron</option>
                  <option value="manual">Manual</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xl={2} lg={4} md={6}>
              <Form.Group>
                <Form.Label>Result</Form.Label>
                <Form.Select
                  className="pb-control"
                  value={generationHistoryFilters.action}
                  onChange={(e) =>
                    setGenerationHistoryFilters((prev) => ({
                      ...prev,
                      action: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="generated">Generated</option>
                  <option value="skipped">Skipped</option>
                  <option value="unsupported">Unsupported</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xl={3} lg={4} md={6}>
              <Form.Group>
                <Form.Label>Panchayath</Form.Label>
                <PanchayathSelector
                  value={generationHistoryFilters.pan_id}
                  allowAll
                  onChange={(value) =>
                    setGenerationHistoryFilters((prev) => ({
                      ...prev,
                      pan_id: value,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            <Col xl={2} lg={4} md={6}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  className="pb-control"
                  type="date"
                  value={generationHistoryFilters.from_date}
                  onChange={(e) =>
                    setGenerationHistoryFilters((prev) => ({
                      ...prev,
                      from_date: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            <Col xl={2} lg={4} md={6}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  className="pb-control"
                  type="date"
                  min={generationHistoryFilters.from_date || undefined}
                  value={generationHistoryFilters.to_date}
                  onChange={(e) =>
                    setGenerationHistoryFilters((prev) => ({
                      ...prev,
                      to_date: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            <Col xl="auto" lg={4} md={6} className="pb-filter-actions">
              <Button
                className="pb-btn pb-btn-primary"
                onClick={searchGenerationHistory}
              >
                <Search size={15} /> Search
              </Button>
              <Button
                className="pb-btn pb-btn-light"
                onClick={resetGenerationHistory}
              >
                <RefreshCcw size={15} /> Reset
              </Button>
            </Col>
          </Row>

          <div className="pb-history-table-wrap">
            <Table hover className="pb-table pb-history-table align-middle mb-0">
              <thead><tr><th>Sl. No.</th><th>Date & Time</th><th>Source</th><th>Panchayath</th><th>Subscription</th><th>Period</th><th>Final Amount</th><th>Result</th><th>Reason</th></tr></thead>
              <tbody>
                {generationHistoryLoading ? <tr><td colSpan={9} className="pb-loading-cell"><Spinner animation="border" size="sm" /> Loading generation history...</td></tr> : generationHistory?.rows?.length ? generationHistory.rows.map((item, index) => <tr key={item.log_id}>
                  <td>{(Number(generationHistoryPagination.page || 1) - 1) * Number(generationHistoryPagination.pageSize || 10) + index + 1}</td>
                  <td>{item.created_at || "-"}</td>
                  <td><span className={`pb-type-pill ${item.run_source === "cron" ? "automatic" : "manual"}`}>{item.run_source === "cron" ? "Cron" : "Manual"}</span></td>
                  <td>{item.pan_name ? <><div className="pb-pan-name">{item.pan_name}</div><small className="pb-pan-id">ID #{item.pan_id}</small></> : "-"}</td>
                  <td>{labelize(item.subscription_type) || "-"}</td><td>{item.billing_period || "-"}</td><td className="pb-money">{item.amount != null ? money(item.amount) : "-"}</td><td><span className={`pb-generation-pill ${item.action}`}>{labelize(item.action)}</span></td><td className="pb-history-reason">{item.reason || "-"}</td>
                </tr>) : <tr><td colSpan={9} className="pb-empty-cell"><strong>No generation history found</strong><span>No records match the selected filters.</span></td></tr>}
              </tbody>
            </Table>
          </div>

          <div className="pb-history-footer">
            <div className="pb-record-count">
              Showing page <strong>{generationHistoryPagination.page || 1}</strong> of{" "}
              <strong>{generationHistoryPagination.totalPages || 1}</strong>
              <span>•</span>
              Total <strong>{generationHistoryPagination.total || 0}</strong> records
            </div>

            {generationHistoryPagination.totalPages > 1 && (
              <Pagination className="pb-pagination mb-0">
                <Pagination.Item
                  disabled={generationHistoryPagination.page <= 1}
                  onClick={() => changeGenerationHistoryPage(1)}
                  aria-label="First page"
                  title="First page"
                >
                  «
                </Pagination.Item>

                <Pagination.Item
                  disabled={generationHistoryPagination.page <= 1}
                  onClick={() =>
                    changeGenerationHistoryPage(
                      generationHistoryPagination.page - 1
                    )
                  }
                  aria-label="Previous page"
                  title="Previous page"
                >
                  ‹
                </Pagination.Item>

                <Pagination.Item active>
                  {generationHistoryPagination.page}
                </Pagination.Item>

                <Pagination.Item
                  disabled={
                    generationHistoryPagination.page >=
                    generationHistoryPagination.totalPages
                  }
                  onClick={() =>
                    changeGenerationHistoryPage(
                      generationHistoryPagination.page + 1
                    )
                  }
                  aria-label="Next page"
                  title="Next page"
                >
                  ›
                </Pagination.Item>

                <Pagination.Item
                  disabled={
                    generationHistoryPagination.page >=
                    generationHistoryPagination.totalPages
                  }
                  onClick={() =>
                    changeGenerationHistoryPage(
                      generationHistoryPagination.totalPages
                    )
                  }
                  aria-label="Last page"
                  title="Last page"
                >
                  »
                </Pagination.Item>
              </Pagination>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer><Button className="pb-btn pb-btn-light" onClick={() => setGenerationHistoryModal(false)}><X size={15} /> Close</Button></Modal.Footer>
      </Modal>

  <Modal
  show={receiptModal}
  onHide={closeReceipts}
  size="xl"
  centered
  className="pb-modal"
>
  <Modal.Header closeButton>
    <div>
      <Modal.Title>
        Receipt History
      </Modal.Title>

      <div className="pb-modal-subtitle">
        Receipts recorded for this bill and their approval status
      </div>
    </div>
  </Modal.Header>

  <Modal.Body>
    {receiptLoading ? (
      <div className="pb-modal-loading">
        <Spinner />
        <span>
          Loading receipts...
        </span>
      </div>
    ) : (
      <>
        {receiptData.bill && (
          <>
            <div className="pb-receipt-head">
              <div>
                <span>Panchayath</span>
                <strong>
                  {receiptData.bill.pan_name || "-"}
                </strong>
              </div>

              <div>
                <span>Invoice</span>
                <strong>
                  {receiptData.bill.invoice_number || "-"}
                </strong>
              </div>

              <div>
                <span>Status</span>

                <span
                  className={`pb-status-pill pb-status-${receiptData.bill.status}`}
                >
                  {labelize(
                    receiptData.bill.status
                  )}
                </span>
              </div>
            </div>

            {isGstBill(receiptData.bill) && (
              <div className="pb-tax-breakup mb-3">
                <span>Bill Tax Breakup</span>
                <strong>
                  Base {money(receiptData.bill.taxable_amount)} + GST {Number(receiptData.bill.tax_percentage || 0)}% ({money(receiptData.bill.tax_amount)}) = {money(receiptData.bill.bill_amount)}
                </strong>
              </div>
            )}

            <Row className="g-2 mb-3">
              <Col xl={3} md={6}>
                <div className="pb-mini-card">
                  <span>
                    {receiptData.bill.invoice_number
                      ? "Invoice Amount"
                      : "Payable Amount"}
                  </span>

                  <strong>
                    {money(
                      receiptData.bill.invoice_amount ??
                        receiptData.bill.bill_amount
                    )}
                  </strong>
                </div>
              </Col>

              <Col xl={3} md={6}>
                <div className="pb-mini-card received">
                  <span>
                    Approved Received
                  </span>

                  <strong>
                    {money(
                      receiptData.bill.approved_received ??
                        receiptData.bill.total_received
                    )}
                  </strong>
                </div>
              </Col>

              <Col xl={3} md={6}>
                <div className="pb-mini-card">
                  <span>
                    Pending Approval
                  </span>

                  <strong>
                    {money(
                      receiptData.bill.pending_received
                    )}
                  </strong>
                </div>
              </Col>

              <Col xl={3} md={6}>
                <div className="pb-mini-card balance">
                  <span>
                    Balance
                  </span>

                  <strong>
                    {money(
                      receiptData.bill.balance_amount
                    )}
                  </strong>
                </div>
              </Col>
            </Row>

            <div className="pb-auto-receipt-number mb-4">
              Amount left to record:{" "}
              <strong>
                {money(
                  receiptData.bill.available_for_submission
                )}
              </strong>

              {Number(
                receiptData.bill.pending_received || 0
              ) > 0 && (
                <>
                  {" "}
                  • Pending receipt reserves{" "}
                  <strong>
                    {money(
                      receiptData.bill.pending_received
                    )}
                  </strong>
                </>
              )}
            </div>
          </>
        )}

        <div className="pb-receipt-table-wrap">
          <Table
            hover
            responsive
            className="pb-receipt-table align-middle mb-0"
          >
            <thead>
              <tr>
                <th>
                  Sl. No.
                </th>

                <th>
                  Receipt No.
                </th>

                <th>
                  Payment Date
                </th>

                <th>
                  Amount
                </th>

                <th>
                  Mode
                </th>

                <th>
                  Reference / Payment Details
                </th>

                <th>
                  Approval Status
                </th>

                <th>
                  Submitted By
                </th>

                <th>
                  Submitted On
                </th>

                <th>
                  Reviewed By
                </th>

                <th>
                  Reviewed On
                </th>

                <th>
                  Remarks / Rejection Reason
                </th>
              </tr>
            </thead>

            <tbody>
              {receiptData.receipts?.length ? (
                receiptData.receipts.map(
                  (
                    item,
                    index
                  ) => (
                    <tr
                      key={
                        item.receipt_id
                      }
                    >
                      {/* SL NO */}
                      <td>
                        {index + 1}
                      </td>

                      {/* RECEIPT NO */}
                      <td>
                        {item.receipt_number ||
                          "-"}
                      </td>

                      {/* PAYMENT DATE */}
                      <td>
                        {item.receipt_date ||
                          "-"}
                      </td>

                      {/* AMOUNT */}
                      <td className="pb-money">
                        {money(
                          item.received_amount
                        )}
                      </td>

                      {/* PAYMENT MODE */}
                      <td>
                        <span className="pb-payment-mode">
                          {labelize(
                            item.payment_mode
                          )}
                        </span>
                      </td>

                      {/* PAYMENT DETAILS */}
                      <td>
                        {item.payment_mode ===
                        "cheque" ? (
                          <div>
                            <div>
                              <strong>
                                Cheque:
                              </strong>{" "}
                              {item.cheque_no ||
                                "-"}
                            </div>

                            {item.bank_name ? (
                              <small className="d-block text-muted">
                                Bank:{" "}
                                {
                                  item.bank_name
                                }
                              </small>
                            ) : null}

                            {item.cheque_date ? (
                              <small className="d-block text-muted">
                                Date:{" "}
                                {
                                  item.cheque_date
                                }
                              </small>
                            ) : null}
                          </div>
                        ) : item.transaction_reference ? (
                          <div>
                            <strong>
                              Ref:
                            </strong>{" "}
                            {
                              item.transaction_reference
                            }
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* APPROVAL STATUS */}
                      <td>
                        <span
                          className={`pb-status-pill pb-status-${
                            item.approval_status ||
                            "pending"
                          }`}
                        >
                          {labelize(
                            item.approval_status ||
                              "pending"
                          )}
                        </span>
                      </td>

                      {/* SUBMITTED BY */}
                      <td>
                        <div>
                          <strong>
                            {item.submitted_by_name ||
                              "-"}
                          </strong>

                          {item.submitted_by ? (
                            <small className="d-block text-muted">
                              User ID:{" "}
                              {
                                item.submitted_by
                              }
                            </small>
                          ) : null}
                        </div>
                      </td>

                      {/* SUBMITTED ON */}
                      <td>
                        {formatDateTime(
                          item.submitted_at ||
                            item.created_at
                        )}
                      </td>

                      {/* REVIEWED BY */}
                      <td>
                        {item.approval_status ===
                        "pending" ? (
                          "-"
                        ) : (
                          <div>
                            <strong>
                              {item.approved_by_name ||
                                "-"}
                            </strong>

                            <small className="d-block text-muted">
                              {item.approval_status ===
                              "rejected"
                                ? "Rejected By"
                                : "Approved By"}
                            </small>
                          </div>
                        )}
                      </td>

                      {/* REVIEWED ON */}
                      <td>
                        {item.approval_status ===
                        "pending"
                          ? "-"
                          : formatDateTime(
                              item.approved_at
                            )}
                      </td>

                      {/* REMARKS / REJECTION */}
                      <td>
                        {item.approval_status ===
                        "rejected" ? (
                          <div>
                            <strong>
                              Reason:
                            </strong>{" "}
                            {item.rejection_reason ||
                              "-"}

                            {item.remarks ? (
                              <small className="d-block text-muted">
                                Remarks:{" "}
                                {
                                  item.remarks
                                }
                              </small>
                            ) : null}
                          </div>
                        ) : (
                          item.remarks ||
                          "-"
                        )}
                      </td>
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={12}
                    className="pb-receipt-empty"
                  >
                    No receipts recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button
      className="pb-btn pb-btn-light"
      onClick={
        closeReceipts
      }
    >
      <X size={15} />
      Close
    </Button>
  </Modal.Footer>
</Modal>
    </div>
  );
};

export default PanchayathBillingPage;
